import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shadcn/components/ui/dialog';
import { Button } from '@shadcn/components/ui/button';
import { Label } from '@shadcn/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shadcn/components/ui/select';
import { Loader2, AlertCircle, CheckCircle } from 'lucide-react';

import { StatusBadge } from '../TicketSystem/StatusBadge';
import { TicketTypeIcon } from '../TicketSystem/TicketTypeIcon';
import {
  META_STATUS,
  TICKET_TYPE_LABELS,
  VALIDATION_RULES,
  FORM_DEFAULTS,
  COMPONENT_THEMES
} from '../../constants';
import ticketApi from '../../services/ticketApi';


const StatusMappingForm = ({
  mapping = null,
  onSubmit,
  onCancel
}) => {
  const [formData, setFormData] = useState(FORM_DEFAULTS.STATUS_MAPPING);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [availableStatusesData, setAvailableStatusesData] = useState(null);
  const [isLoadingStatuses, setIsLoadingStatuses] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const isEditing = !!mapping;

  /**
   * Fetch available statuses when form opens
   */
  useEffect(() => {
    const fetchAvailableStatuses = async () => {
      setIsLoadingStatuses(true);
      try {
        const data = await ticketApi.getAvailableStatuses();
        setAvailableStatusesData(data);
      } catch (error) {
        console.error('Failed to fetch available statuses:', error);
        setErrors(prev => ({ ...prev, api: 'Failed to load available statuses' }));
      } finally {
        setIsLoadingStatuses(false);
      }
    };

    fetchAvailableStatuses();
  }, []);

  /**
   * Initialize form data
   */
  useEffect(() => {
    if (mapping) {
      setFormData({
        ticketType: mapping.ticketType || '',
        originalStatus: mapping.originalStatus || '',
        metaStatus: mapping.metaStatus || '',
        boardId: mapping.boardId || ''
      });
    } else {
      setFormData(FORM_DEFAULTS.STATUS_MAPPING);
    }
    setErrors({});
  }, [mapping]);

  /**
   * Handle input change
   */
  const handleInputChange = (field, value) => {
    const updatedFormData = { ...formData, [field]: value };
    
    // Auto-select BoardId when Ticket Type changes
    if (field === 'ticketType' && value && availableStatusesData?.boards) {
      const selectedBoard = availableStatusesData.boards.find(
        board => board.ticketType === value
      );
      if (selectedBoard) {
        updatedFormData.boardId = selectedBoard.boardId;
      }
      // Clear originalStatus when ticket type changes
      updatedFormData.originalStatus = '';
    }
    
    setFormData(updatedFormData);
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
    
    // Show preview when all required fields are filled
    if (field === 'metaStatus' && value && updatedFormData.ticketType && updatedFormData.originalStatus) {
      setShowPreview(true);
    }
  };

  /**
   * Validate form
   */
  const validateForm = () => {
    const newErrors = {};

    if (!formData.ticketType) {
      newErrors.ticketType = VALIDATION_RULES.TICKET_TYPE_REQUIRED;
    }

    if (!formData.originalStatus.trim()) {
      newErrors.originalStatus = VALIDATION_RULES.ORIGINAL_STATUS_REQUIRED;
    }

    if (!formData.metaStatus) {
      newErrors.metaStatus = VALIDATION_RULES.META_STATUS_REQUIRED;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submit
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const result = await onSubmit(formData);
      
      if (result.success) {
        setSubmitSuccess(true);
        setErrors({});
        // Close form after a brief success display
        setTimeout(() => {
          onCancel();
        }, 1500);
      } else {
        // Handle server-side validation errors
        if (result.error.includes('already exists')) {
          setErrors({ originalStatus: 'This mapping already exists' });
        } else {
          setErrors({ submit: result.error });
        }
      }
    } catch (error) {
      setErrors({ submit: 'An unexpected error occurred' });
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Get available original statuses for selected ticket type
   */
  const getOriginalStatusSuggestions = () => {
    if (!formData.ticketType || !availableStatusesData) {
      return [];
    }

    const board = availableStatusesData.boards?.find(
      board => board.ticketType === formData.ticketType
    );

    return board ? board.statuses : [];
  };

  /**
   * Get available ticket types from API data
   */
  const getAvailableTicketTypes = () => {
    if (!availableStatusesData?.boards) {
      return [];
    }

    // Get unique ticket types to avoid duplicates
    const uniqueTypes = [...new Set(availableStatusesData.boards.map(board => board.ticketType))];
    
    return uniqueTypes.map(type => ({
      type,
      label: TICKET_TYPE_LABELS[type] || type
    }));
  };





  return (
    <Dialog open={true} onOpenChange={onCancel}>
      {isLoadingStatuses ? (
        <DialogContent className={COMPONENT_THEMES.dialog.content}>
          <DialogHeader className={COMPONENT_THEMES.dialog.header}>
            <DialogTitle className={COMPONENT_THEMES.dialog.title}>Loading...</DialogTitle>
          </DialogHeader>
        </DialogContent>
      ) : (
      <DialogContent className={`w-full max-w-md sm:max-w-lg md:max-w-xl mx-4 ${COMPONENT_THEMES.dialog.content}`}>
        <DialogHeader className={COMPONENT_THEMES.dialog.header}>
          <DialogTitle className={COMPONENT_THEMES.dialog.title}>
            {isEditing ? 'Edit Status Mapping' : 'Create Status Mapping'}
          </DialogTitle>
          <DialogDescription className={COMPONENT_THEMES.dialog.description}>
            {isEditing 
              ? 'Update the status mapping configuration.'
              : 'Create a new mapping between an original status and a meta-status.'
            }
          </DialogDescription>
        </DialogHeader>

        {/* API Error Display */}
        {errors.api && (
          <div className={`p-3 rounded-md ${COMPONENT_THEMES.form.error}`}>
            <p className="text-sm flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.api}
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Ticket Type */}
          <div className="space-y-2">
            <Label htmlFor="ticketType" className={COMPONENT_THEMES.form.label}>Ticket Type *</Label>
            <Select
              value={formData.ticketType}
              onValueChange={(value) => handleInputChange('ticketType', value)}
            >
              <SelectTrigger className={COMPONENT_THEMES.form.select}>
                <SelectValue placeholder="Select ticket type" />
              </SelectTrigger>
              <SelectContent>
                {getAvailableTicketTypes().map(({ type, label }) => (
                  <SelectItem key={type} value={type}>
                    <div className="flex items-center gap-2">
                      <TicketTypeIcon type={type} size="xs" showBackground={false} />
                      {label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.ticketType && (
              <p className={`text-sm flex items-center gap-1 ${COMPONENT_THEMES.form.error}`}>
                <AlertCircle className="w-3 h-3" />
                {errors.ticketType}
              </p>
            )}
          </div>

          {/* Original Status */}
          <div className="space-y-2">
            <Label htmlFor="originalStatus" className={COMPONENT_THEMES.form.label}>Original Status *</Label>
            <Select
              value={formData.originalStatus}
              onValueChange={(value) => handleInputChange('originalStatus', value)}
              disabled={isLoadingStatuses || !formData.ticketType}
            >
              <SelectTrigger className={COMPONENT_THEMES.form.select}>
                <SelectValue placeholder={
                  isLoadingStatuses 
                    ? "Loading statuses..." 
                    : !formData.ticketType 
                      ? "Select ticket type first"
                      : "Select original status"
                } />
              </SelectTrigger>
              <SelectContent>
                {!isLoadingStatuses && formData.ticketType && getOriginalStatusSuggestions().length > 0 ? (
                  getOriginalStatusSuggestions().map(status => (
                    <SelectItem key={status} value={status}>
                      <code className="bg-gray-100 px-2 py-1 rounded text-sm">
                        {status}
                      </code>
                    </SelectItem>
                  ))
                ) : formData.ticketType && !isLoadingStatuses ? (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    No statuses available for {TICKET_TYPE_LABELS[formData.ticketType]}
                  </div>
                ) : null}
              </SelectContent>
            </Select>
            
            {errors.originalStatus && (
              <p className={`text-sm flex items-center gap-1 ${COMPONENT_THEMES.form.error}`}>
                <AlertCircle className="w-3 h-3" />
                {errors.originalStatus}
              </p>
            )}
          </div>

          {/* Meta Status */}
          <div className="space-y-2">
            <Label htmlFor="metaStatus" className={COMPONENT_THEMES.form.label}>Meta Status *</Label>
            <Select
              value={formData.metaStatus}
              onValueChange={(value) => handleInputChange('metaStatus', value)}
            >
              <SelectTrigger className={COMPONENT_THEMES.form.select}>
                <SelectValue placeholder="Select meta status" />
              </SelectTrigger>
              <SelectContent>
                {Object.values(META_STATUS).map(status => (
                  <SelectItem key={status} value={status}>
                    <div className="flex items-center gap-2">
                      <StatusBadge 
                        metaStatus={status} 
                        showOriginal={false}
                      />
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.metaStatus && (
              <p className={`text-sm flex items-center gap-1 ${COMPONENT_THEMES.form.error}`}>
                <AlertCircle className="w-3 h-3" />
                {errors.metaStatus}
              </p>
            )}
          </div>

          {/* Board ID (Hidden) */}
          <input type="hidden" name="boardId" value={formData.boardId} />

          {/* Preview */}
          {showPreview && formData.ticketType && formData.originalStatus && formData.metaStatus && (
            <div className={`p-4 rounded-lg space-y-2 ${COMPONENT_THEMES.form.success}`}>
              <h4 className="font-medium text-sm">Preview:</h4>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <TicketTypeIcon type={formData.ticketType} size="sm" />
                  <span className="truncate">{TICKET_TYPE_LABELS[formData.ticketType]}</span>
                </div>
                <span className="hidden sm:inline">→</span>
                <code className="bg-white px-2 py-1 rounded border border-gray-200 break-all text-xs sm:text-sm">
                  {formData.originalStatus}
                </code>
                <span className="hidden sm:inline">→</span>
                <StatusBadge 
                  metaStatus={formData.metaStatus}
                  showOriginal={false}
                />
              </div>
            </div>
          )}

                  {/* Submit Success */}
        {submitSuccess && (
          <div className={`p-3 rounded-md ${COMPONENT_THEMES.form.success}`}>
            <p className="text-sm flex items-center gap-1">
              <CheckCircle className="w-4 h-4" />
              {isEditing ? 'Mapping updated successfully!' : 'Mapping created successfully!'}
            </p>
          </div>
        )}

        {/* Submit Error */}
        {errors.submit && (
          <div className={`p-3 rounded-md ${COMPONENT_THEMES.form.error}`}>
            <p className="text-sm flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.submit}
            </p>
          </div>
        )}
        </form>

        <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting}
            className={`w-full sm:w-auto ${COMPONENT_THEMES.form.label}`}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            onClick={handleSubmit}
            disabled={isSubmitting || isLoadingStatuses || !formData.ticketType || !formData.originalStatus || !formData.metaStatus}
            className="w-full sm:w-auto bg-black text-white hover:bg-gray-800 disabled:bg-gray-400"
          >
            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isSubmitting 
              ? (isEditing ? 'Updating...' : 'Creating...')
              : (isEditing ? 'Update Mapping' : 'Create Mapping')
            }
          </Button>
        </DialogFooter>
      </DialogContent>
      )}
    </Dialog>
  );
};

export default StatusMappingForm;
